import React, { useState, useEffect } from 'react';
import Layout from '../components/layout/Layout';
import { API_BASE } from '../api';

// 🎨 GLOBAL COLORS DEFINITION 
const colors = {
    primary: '#6366F1',
    secondary: '#8B5CF6',
    accent: '#06B6D4',
    success: '#10B981',
    warning: '#F59E0B',
    danger: '#EF4444',
    dark: '#1E293B',
    light: '#F8FAFC',
    gradientMain: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    gradient2: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    gradient3: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    gradient4: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)'
};

function ModifyClients() {
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]); // For search results
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    
    // Search State
    const [searchTerm, setSearchTerm] = useState('');
    const [searchCategory, setSearchCategory] = useState('all'); // 'all', 'name', 'email', 'id'
    
    // Password Modal States
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [newPassword, setNewPassword] = useState('');
    const [updating, setUpdating] = useState(false);
    
    // Edit Modal States
    const [showEditModal, setShowEditModal] = useState(false);
    const [editFormData, setEditFormData] = useState({ 
        id: null,
        name: '',
        email: '',
        status: '',
        description: '',
        socialMediaProfile: ''
    });

    // Delete States
    const [deletingUser, setDeletingUser] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);

    const [togglingStatus, setTogglingStatus] = useState(false);


    // ✅ Fetch clients when component mounts
    const fetchUsers = async () => {
        try {
            setLoading(true);
            setError('');
            console.log('🔄 Fetching clients from:', `${API_BASE}/get_users.php?type=clients`);
            
            const response = await fetch(`${API_BASE}/get_users.php?type=clients`);
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const result = await response.json();
            console.log('📊 API Response:', result);
            
            if (result.success && Array.isArray(result.data)) {
                const sanitizedUsers = result.data.map(user => ({
                    ...user,
                    description: user.description || user.client_description || 'No description provided', 
                    social_media_profile: user.social_media_profile || 'Not provided',
                    status: user.status || 'Active'
                }));
                
                console.log(`📊 Total clients loaded: ${sanitizedUsers.length}`);
                
                setUsers(sanitizedUsers);
                setFilteredUsers(sanitizedUsers); // Initialize filtered users with all users
            } else {
                throw new Error(result.message || 'Invalid response format from server');
            }
        } catch (error) {
            console.error('❌ Fetch error:', error);
            setError('Cannot connect to server. Please make sure your PHP server is running. Error: ' + error.message);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    // ✅ Search Functionality
    useEffect(() => {
        if (!searchTerm.trim()) {
            setFilteredUsers(users);
            return;
        }

        const searchLower = searchTerm.toLowerCase().trim();
        
        const results = users.filter(user => {
            switch(searchCategory) {
                case 'name':
                    return user.name?.toLowerCase().includes(searchLower);
                case 'email':
                    return user.email?.toLowerCase().includes(searchLower);
                case 'id':
                    return user.id?.toString().includes(searchLower);
                case 'status':
                    return user.status?.toLowerCase().includes(searchLower);
                case 'all':
                default:
                    return (
                        user.name?.toLowerCase().includes(searchLower) ||
                        user.email?.toLowerCase().includes(searchLower) ||
                        user.id?.toString().includes(searchLower) ||
                        user.status?.toLowerCase().includes(searchLower) ||
                        user.description?.toLowerCase().includes(searchLower)
                    );
            }
        });

        setFilteredUsers(results);
    }, [searchTerm, searchCategory, users]);

    const handleSearch = (e) => {
        e.preventDefault();
        // Search is already handled by useEffect
    };

    const handleClearSearch = () => {
        setSearchTerm('');
        setSearchCategory('all');
        setFilteredUsers(users);
    };

    const handleRefresh = () => {
        setRefreshing(true);
        setError('');
        setSuccessMessage('');
        setSearchTerm('');
        setSearchCategory('all');
        fetchUsers();
    };
    
    // ✅ ADDED: Delete user function
    const handleDeleteUser = async (user) => {
        console.log('🗑️ Delete user clicked:', user);
        setUserToDelete(user);
        setShowDeleteModal(true);
    };

    // ✅ ADDED: Confirm and execute delete
    const confirmDeleteUser = async () => {
        if (!userToDelete) return;

        try {
            setDeletingUser(userToDelete.id);
            setSuccessMessage('');
            setError('');
            
            console.log('🔄 Deleting user:', userToDelete.id, 'Type: clients');
            
            const response = await fetch(`${API_BASE}/deleteuser.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    user_id: userToDelete.id,
                    user_type: 'clients'
                })
            });

            const data = await response.json();
            console.log('📥 Delete response:', data);
            
            if (data.success) {
                setSuccessMessage(`✅ Client "${userToDelete.name}" deleted successfully!`);
                
                // Update local state by removing the deleted user
                const updatedUsers = users.filter(user => user.id !== userToDelete.id);
                setUsers(updatedUsers);
                setFilteredUsers(updatedUsers);
                
                // Close modal
                setShowDeleteModal(false);
                setUserToDelete(null);
                
                // Clear message after 3 seconds
                setTimeout(() => {
                    setSuccessMessage('');
                }, 3000);
            } else {
                throw new Error(data.message || 'Failed to delete user');
            }
        } catch (error) {
            console.error('❌ Error deleting user:', error);
            setError(`❌ Error: ${error.message}`);
        } finally {
            setDeletingUser(null);
        }
    };
    
    // ✅ Handler: Show Edit Modal
    const handleEditProfile = (user) => {
        console.log('📝 Editing profile for:', user);
        setEditFormData({
            id: user.id,
            name: user.name,
            email: user.email,
            status: user.status,
            description: user.description || '', 
            socialMediaProfile: user.social_media_profile || ''
        });
        setSuccessMessage('');
        setError('');
        setShowEditModal(true);
    };

    // ✅ Handler: Update User Profile (Now includes email update)
    const handleUpdateProfile = async (e) => {
        e.preventDefault();
        setUpdating(true);
        setError('');
        setSuccessMessage('');

        try {
            const dataToSend = {
                operation: 'update_client_profile',
                userId: editFormData.id,
                name: editFormData.name,
                email: editFormData.email, // Added email
                status: editFormData.status,
                clientDescription: editFormData.description,
                socialMediaProfile: editFormData.socialMediaProfile,
            };

            console.log('📤 Sending update data:', dataToSend);

            const response = await fetch(`${API_BASE}/update_client_profile.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(dataToSend)
            });

            const result = await response.json();
            console.log('📥 Update response:', result);

            if (result.success) {
                setSuccessMessage(`✅ Profile for ${editFormData.name} updated successfully!`);
                setShowEditModal(false);
                await fetchUsers(); 
            } else {
                throw new Error(result.message || 'Failed to update profile');
            }
        } catch (error) {
            console.error('❌ Update profile error:', error);
            setError('❌ Failed to update profile: ' + error.message);
        } finally {
            setUpdating(false);
        }
    };

    // ✅ Handler: Edit Password
    const handleEditPassword = (user) => {
        console.log('🔑 Editing password for:', user.name);
        setSelectedUser(user);
        setNewPassword('');
        setError('');
        setSuccessMessage('');
        setShowPasswordModal(true);
    };

    // ✅ Handler: Update Password
    const handleUpdatePassword = async () => {
        if (!newPassword || newPassword.length < 6) {
            setError('❌ Password must be at least 6 characters long');
            return;
        }

        try {
            setUpdating(true);
            setError('');
            
            console.log('📤 Updating password for user:', selectedUser.id);
            
            const response = await fetch(`${API_BASE}/update_password.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    userType: 'clients',
                    userId: selectedUser.id,
                    newPassword: newPassword
                })
            });

            const result = await response.json();
            console.log('📥 Password update response:', result);

            if (result.success) {
                setSuccessMessage('✅ Password updated successfully!');
                setShowPasswordModal(false);
                setSelectedUser(null);
                setNewPassword('');
            } else {
                throw new Error(result.message || 'Failed to update password');
            }
        } catch (error) {
            console.error('❌ Update error:', error);
            setError('❌ Failed to update password: ' + error.message);
        } finally {
            setUpdating(false);
        }
    };

    // ✅ Handler: Toggle Status
    const handleToggleStatus = async (user) => {
        try {
            setTogglingStatus(true);
            setError('');
            const newStatus = user.status === 'Active' ? 'Inactive' : 'Active';
            
            console.log('🔄 Toggling status for user:', user.id, 'to:', newStatus);
            
            const response = await fetch(`${API_BASE}/update_status.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    userType: 'clients',
                    userId: user.id,
                    newStatus: newStatus
                })
            });

            const result = await response.json();
            console.log('📥 Status update response:', result);

            if (result.success) {
                setSuccessMessage(`✅ Status updated to ${newStatus} for ${user.name}`);
                // Update users
                const updatedUsers = users.map(u => 
                    u.id === user.id ? { ...u, status: newStatus } : u
                );
                setUsers(updatedUsers);
                setFilteredUsers(updatedUsers);
            } else {
                throw new Error(result.message || 'Failed to update status');
            }
        } catch (error) {
            console.error('❌ Status update error:', error);
            setError('❌ Failed to update status: ' + error.message);
        } finally {
            setTogglingStatus(false);
        }
    };

    // ✅ Status Badge Style
    const getStatusBadgeStyle = (status) => {
        const baseStyle = {
            padding: '4px 12px',
            borderRadius: '20px',
            fontSize: '0.75rem',
            fontWeight: '600',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px'
        };

        if (status === 'Active') {
            return {
                ...baseStyle,
                background: colors.success,
                color: 'white'
            };
        } else {
            return {
                ...baseStyle,
                background: '#6B7280',
                color: 'white'
            };
        }
    };

    // ✅ Action Button Styles
    const getActionButtonStyle = (type) => {
        const baseStyle = {
            padding: '6px 12px',
            borderRadius: '8px',
            fontSize: '0.75rem',
            fontWeight: '600',
            border: 'none',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            transition: 'all 0.3s ease'
        };

        switch (type) {
            case 'edit':
                return {
                    ...baseStyle,
                    background: colors.accent,
                    color: 'white',
                    border: `1px solid ${colors.accent}`,
                };
            case 'password':
                return {
                    ...baseStyle,
                    background: 'rgba(99, 102, 241, 0.1)',
                    color: colors.primary,
                    border: `1px solid rgba(99, 102, 241, 0.3)`
                };
            case 'status':
                return {
                    ...baseStyle,
                    background: colors.warning,
                    color: colors.dark,
                    border: `1px solid ${colors.warning}`,
                };
            case 'delete':
                return {
                    ...baseStyle,
                    background: `linear-gradient(135deg, ${colors.danger}, #dc2626)`,
                    color: 'white',
                    border: `1px solid ${colors.danger}`,
                };
            default:
                return baseStyle;
        }
    };

    return (
        <Layout>
            <div className="modify-clients-container" style={styles.container}>
                {/* Animated Background */}
                <div className="animated-background" style={styles.animatedBackground}>
                    <div className="floating-orb orb-1" style={{...styles.floatingOrb, ...styles.orb1}}></div>
                    <div className="floating-orb orb-2" style={{...styles.floatingOrb, ...styles.orb2}}></div>
                    <div className="floating-orb orb-3" style={{...styles.floatingOrb, ...styles.orb3}}></div>
                    <div className="floating-orb orb-4" style={{...styles.floatingOrb, ...styles.orb4}}></div>
                    <div className="grid-overlay" style={styles.gridOverlay}></div>
                </div>

                {/* Header Section */}
                <div className="clients-header" style={styles.header}>
                    <div className="header-content" style={styles.headerContent}>
                        <div className="welcome-section">
                            <div className="greeting" style={styles.greeting}>
                                <h1 className="page-title" style={styles.pageTitle}>
                                    <i className="fas fa-user-friends me-2"></i>
                                    Clients Management
                                </h1>
                                <div className="welcome-badge" style={styles.welcomeBadge}>
                                    <span className="badge-text" style={styles.badgeText}>Admin Panel</span>
                                    <span className="badge-role" style={styles.badgeRole}>Manager</span>
                                </div>
                            </div>
                            <p className="page-subtitle" style={styles.pageSubtitle}>
                                Manage and monitor all client accounts and their status
                            </p>
                        </div>
                        <div className="header-actions" style={styles.headerActions}>
                            <div className="client-count" style={styles.clientCount}>
                                <span style={styles.clientCountText}>Showing:</span>
                                <span style={styles.clientCountNumber}>{filteredUsers.length} of {users.length}</span>
                            </div>
                            <button 
                                className="refresh-btn"
                                onClick={handleRefresh}
                                disabled={refreshing || loading}
                                style={{
                                    ...styles.refreshBtn,
                                    opacity: (refreshing || loading) ? 0.6 : 1,
                                    cursor: (refreshing || loading) ? 'not-allowed' : 'pointer'
                                }}
                            >
                                <div className="refresh-icon" style={styles.refreshIcon}>
                                    <i className={`fas fa-sync ${refreshing ? 'spinning' : ''}`}></i>
                                </div>
                                <span>{refreshing ? 'Refreshing...' : 'Refresh Data'}</span>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Success Message */}
                {successMessage && (
                    <div className="success-alert" style={{...styles.successAlert, margin: '0 40px 30px', maxWidth: '1400px', marginLeft: 'auto', marginRight: 'auto'}}>
                        <div className="alert-icon" style={{...styles.alertIcon, color: colors.success}}>
                            <i className="fas fa-check-circle"></i>
                        </div>
                        <div className="alert-message" style={styles.alertMessage}>
                            <strong>Success</strong>
                            <span>{successMessage}</span>
                        </div>
                        <button className="alert-close" onClick={() => setSuccessMessage('')} style={{...styles.alertClose, color: colors.success}}>
                            <i className="fas fa-times"></i>
                        </button>
                    </div>
                )}
                
                {/* Error Message */}
                {error && (
                    <div className="error-alert" style={styles.errorAlert}>
                        <div className="alert-icon" style={styles.alertIcon}>
                            <i className="fas fa-exclamation-triangle"></i>
                        </div>
                        <div className="alert-message" style={styles.alertMessage}>
                            <strong>Error</strong>
                            <span>{error}</span>
                        </div>
                        <button className="alert-close" onClick={() => setError('')} style={styles.alertClose}>
                            <i className="fas fa-times"></i>
                        </button>
                    </div>
                )}

                {/* Main Content */}
                <div className="clients-content" style={styles.content}>
                    {/* Search Section */}
                    <div className="search-section" style={styles.searchSection}>
                        <div className="search-card" style={styles.searchCard}>
                            <h3 style={styles.searchTitle}>
                                <i className="fas fa-search me-2"></i>
                                Search Clients
                            </h3>
                            <form onSubmit={handleSearch} style={styles.searchForm}>
                                <div className="search-input-group" style={styles.searchInputGroup}>
                                    <div className="search-field" style={styles.searchField}>
                                        <i className="fas fa-search" style={styles.searchIcon}></i>
                                        <input
                                            type="text"
                                            placeholder="Search clients..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            style={styles.searchInput}
                                        />
                                        {searchTerm && (
                                            <button
                                                type="button"
                                                onClick={handleClearSearch}
                                                style={styles.clearSearchBtn}
                                                title="Clear search"
                                            >
                                                <i className="fas fa-times"></i>
                                            </button>
                                        )}
                                    </div>
                                    
                                    <select
                                        value={searchCategory}
                                        onChange={(e) => setSearchCategory(e.target.value)}
                                        style={styles.searchSelect}
                                    >
                                        <option value="all">All Fields</option>
                                        <option value="name">Name</option>
                                        <option value="email">Email</option>
                                        <option value="id">ID</option>
                                        <option value="status">Status</option>
                                    </select>
                                    
                                    <button 
                                        type="submit"
                                        style={styles.searchButton}
                                        disabled={loading}
                                    >
                                        <i className="fas fa-search me-2"></i>
                                        Search
                                    </button>
                                </div>
                                
                                {searchTerm && (
                                    <div style={styles.searchResultsInfo}>
                                        <span style={styles.searchResultsText}>
                                            Found <strong>{filteredUsers.length}</strong> client{filteredUsers.length !== 1 ? 's' : ''} 
                                            matching "{searchTerm}" in {searchCategory === 'all' ? 'all fields' : searchCategory}
                                        </span>
                                    </div>
                                )}
                            </form>
                        </div>
                    </div>

                    {/* Clients Table Section */}
                    <div className="clients-table-section" style={styles.clientsTableSection}>
                        <div className="table-card" style={styles.tableCard}>
                            <div className="table-card-header" style={styles.tableCardHeader}>
                                <h3 style={styles.tableCardTitle}>
                                    <i className="fas fa-table me-2"></i>
                                    {searchTerm ? 'Search Results' : 'All Clients Records'}
                                    <span style={styles.filterBadge}>
                                        {searchTerm ? `${filteredUsers.length} found` : `Showing ${filteredUsers.length} clients`}
                                    </span>
                                </h3>
                            </div>
                            <div className="table-card-body" style={styles.tableCardBody}>
                                {loading ? (
                                    <div className="loading-state" style={styles.loadingState}>
                                        <div className="loading-spinner" style={styles.loadingSpinner}></div>
                                        <h3 style={styles.loadingText}>Loading Clients</h3>
                                        <p style={styles.loadingSubtext}>Please wait while we fetch client data</p>
                                    </div>
                                ) : filteredUsers.length === 0 ? (
                                    <div className="empty-state" style={styles.emptyState}>
                                        <div className="empty-icon" style={styles.emptyIcon}>
                                            <i className="fas fa-users"></i>
                                        </div>
                                        <h3 style={styles.emptyTitle}>
                                            {searchTerm ? 'No Clients Found' : 'No Clients Found'}
                                        </h3>
                                        <p style={styles.emptyText}>
                                            {searchTerm 
                                                ? `No clients found matching "${searchTerm}". Try a different search term.`
                                                : 'Clients will appear here when they are added to the system'
                                            }
                                        </p>
                                        {searchTerm && (
                                            <button 
                                                onClick={handleClearSearch}
                                                style={styles.clearSearchBtn}
                                            >
                                                <i className="fas fa-times me-2"></i>
                                                Clear Search
                                            </button>
                                        )}
                                        <button 
                                            onClick={handleRefresh}
                                            style={styles.refreshBtn}
                                        >
                                            <i className="fas fa-sync me-2"></i>
                                            Refresh
                                        </button>
                                    </div>
                                ) : (
                                    <div className="table-container" style={styles.tableContainer}>
                                        <table className="clients-table" style={styles.clientsTable}>
                                            <thead>
                                                <tr>
                                                    {['ID', 'Full Name', 'Email Address', 'Status', 'Description', 'Social Media', 'Actions'].map((header, index) => (
                                                        <th key={index} style={styles.tableHeader}>
                                                            {header}
                                                        </th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredUsers.map(user => (
                                                    <tr key={user.id} style={styles.tableRow}>
                                                        <td style={styles.tableCell}>
                                                            <span style={styles.userId}>#{user.id}</span>
                                                        </td>
                                                        <td style={styles.tableCell}>
                                                            <div className="user-info" style={styles.userInfo}>
                                                                <div className="user-avatar" style={styles.userAvatar}>
                                                                    <i className="fas fa-user"></i>
                                                                </div>
                                                                <div className="user-details" style={styles.userDetails}>
                                                                    <div className="user-name" style={styles.userName}>{user.name}</div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td style={styles.tableCell}>
                                                            <span style={styles.userEmail}>{user.email}</span>
                                                        </td>
                                                        <td style={styles.tableCell}>
                                                            <span className="status-badge" style={getStatusBadgeStyle(user.status)}>
                                                                <i className={`fas ${
                                                                    user.status === 'Active' ? 'fa-toggle-on' : 'fa-toggle-off'
                                                                }`}></i>
                                                                {user.status}
                                                            </span>
                                                        </td>
                                                        <td style={styles.tableCell}>
                                                            <span style={styles.descriptionText}>
                                                                {user.description && user.description.length > 50 
                                                                    ? `${user.description.substring(0, 50)}...` 
                                                                    : user.description
                                                                }
                                                            </span>
                                                        </td>
                                                        <td style={styles.tableCell}>
                                                            {user.social_media_profile && user.social_media_profile !== 'Not provided' ? (
                                                                <a 
                                                                    href={user.social_media_profile} 
                                                                    target="_blank" 
                                                                    rel="noopener noreferrer" 
                                                                    style={styles.socialLink}
                                                                >
                                                                    View Profile <i className="fas fa-external-link-alt" style={{fontSize: '0.75rem'}}></i>
                                                                </a>
                                                            ) : 'Not provided'}
                                                        </td>
                                                        
                                                        <td style={styles.tableCell}>
                                                            <div className="action-buttons" style={{...styles.actionButtons, flexWrap: 'wrap', gap: '6px'}}>
                                                                <button
                                                                    className="edit-profile-btn"
                                                                    onClick={() => handleEditProfile(user)}
                                                                    style={getActionButtonStyle('edit')}
                                                                    title="Edit client profile"
                                                                >
                                                                    <i className="fas fa-user-edit me-1"></i>
                                                                    Edit
                                                                </button>
                                                                
                                                                <button
                                                                    className="password-btn"
                                                                    onClick={() => handleEditPassword(user)}
                                                                    style={getActionButtonStyle('password')}
                                                                    title="Change password"
                                                                >
                                                                    <i className="fas fa-key me-1"></i>
                                                                    Password
                                                                </button>
                                                                
                                                                <button
                                                                    className="status-btn"
                                                                    onClick={() => handleToggleStatus(user)}
                                                                    disabled={togglingStatus}
                                                                    style={{
                                                                        ...getActionButtonStyle('status'),
                                                                        opacity: togglingStatus ? 0.6 : 1,
                                                                        cursor: togglingStatus ? 'not-allowed' : 'pointer'
                                                                    }}
                                                                    title={user.status === 'Active' ? 'Deactivate client' : 'Activate client'}
                                                                >
                                                                    {togglingStatus ? (
                                                                        <span className="spinner" style={styles.spinner}></span>
                                                                    ) : (
                                                                        <i className={`fas ${
                                                                            user.status === 'Active' ? 'fa-toggle-on' : 'fa-toggle-off'
                                                                        } me-1`}></i>
                                                                    )}
                                                                    {user.status === 'Active' ? 'Deactivate' : 'Activate'}
                                                                </button>

                                                                {/* ✅ ADDED: Delete Button */}
                                                                <button
                                                                    className="delete-btn"
                                                                    onClick={() => handleDeleteUser(user)}
                                                                    disabled={deletingUser === user.id}
                                                                    style={{
                                                                        ...getActionButtonStyle('delete'),
                                                                        opacity: deletingUser === user.id ? 0.6 : 1,
                                                                        cursor: deletingUser === user.id ? 'not-allowed' : 'pointer'
                                                                    }}
                                                                    title="Delete client"
                                                                >
                                                                    {deletingUser === user.id ? (
                                                                        <>
                                                                            <span className="spinner" style={styles.spinner}></span>
                                                                            Deleting...
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <i className="fas fa-trash me-1"></i>
                                                                            Delete
                                                                        </>
                                                                    )}
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                            {filteredUsers.length > 0 && (
                                <div className="table-footer" style={styles.tableFooter}>
                                    <div className="footer-content" style={styles.footerContent}>
                                        <span style={styles.footerText}>
                                            {searchTerm 
                                                ? `Showing ${filteredUsers.length} of ${users.length} client records (${searchTerm ? 'filtered' : 'all'})`
                                                : `Showing ${filteredUsers.length} client records`
                                            }
                                        </span>
                                        <span style={styles.footerText}>
                                            Last updated: {new Date().toLocaleTimeString()}
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Delete Confirmation Modal */}
                {showDeleteModal && userToDelete && (
                    <div className="modal-overlay" style={styles.modalOverlay}>
                        <div className="modal-container" style={styles.modalContainer}>
                            <div className="modal-card" style={styles.modalCard}>
                                <div className="modal-header" style={styles.modalHeader}>
                                    <h3 style={styles.modalTitle}>
                                        <i className="fas fa-trash-alt me-2"></i>
                                        Delete Client
                                    </h3>
                                    <button
                                        className="modal-close"
                                        onClick={() => {
                                            setShowDeleteModal(false);
                                            setUserToDelete(null);
                                        }}
                                        style={styles.modalClose}
                                    >
                                        <i className="fas fa-times"></i>
                                    </button>
                                </div>
                                <div className="modal-body" style={styles.modalBody}>
                                    <div className="warning-alert" style={styles.warningAlert}>
                                        <div className="alert-icon" style={styles.warningAlertIcon}>
                                            <i className="fas fa-exclamation-triangle"></i>
                                        </div>
                                        <div className="alert-message" style={styles.warningAlertMessage}>
                                            <strong>Warning: This action cannot be undone!</strong>
                                            <span>Are you sure you want to delete client: <strong>{userToDelete.name}</strong>?</span>
                                            <span style={{fontSize: '0.9rem', marginTop: '5px', color: 'rgba(255, 255, 255, 0.8)'}}>
                                                This will permanently delete all data associated with this client.
                                            </span>
                                        </div>
                                    </div>
                                    
                                    <div className="user-details-preview" style={styles.userDetailsPreview}>
                                        <div style={styles.userDetailItem}>
                                            <span style={styles.userDetailLabel}>Client ID:</span>
                                            <span style={styles.userDetailValue}>#{userToDelete.id}</span>
                                        </div>
                                        <div style={styles.userDetailItem}>
                                            <span style={styles.userDetailLabel}>Email:</span>
                                            <span style={styles.userDetailValue}>{userToDelete.email}</span>
                                        </div>
                                        <div style={styles.userDetailItem}>
                                            <span style={styles.userDetailLabel}>Status:</span>
                                            <span style={styles.userDetailValue}>
                                                <span style={getStatusBadgeStyle(userToDelete.status)}>
                                                    {userToDelete.status}
                                                </span>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer" style={styles.modalFooter}>
                                    <button
                                        className="cancel-btn"
                                        onClick={() => {
                                            setShowDeleteModal(false);
                                            setUserToDelete(null);
                                        }}
                                        disabled={deletingUser === userToDelete.id}
                                        style={styles.cancelBtn}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        className="delete-confirm-btn"
                                        onClick={confirmDeleteUser}
                                        disabled={deletingUser === userToDelete.id}
                                        style={{
                                            ...styles.deleteConfirmBtn,
                                            background: `linear-gradient(135deg, ${colors.danger}, #dc2626)`,
                                            opacity: deletingUser === userToDelete.id ? 0.6 : 1,
                                            cursor: deletingUser === userToDelete.id ? 'not-allowed' : 'pointer'
                                        }}
                                    >
                                        {deletingUser === userToDelete.id ? (
                                            <>
                                                <span className="spinner" style={styles.spinner}></span>
                                                Deleting...
                                            </>
                                        ) : (
                                            'Confirm Delete'
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Edit Profile Modal */}
                {showEditModal && (
                    <div className="modal-overlay" style={styles.modalOverlay}>
                        <div className="modal-container" style={{...styles.modalContainer, maxWidth: '800px', width: '90vw'}}>
                            <div className="modal-card" style={styles.modalCard}>
                                <div className="modal-header" style={styles.modalHeader}>
                                    <h3 style={styles.modalTitle}>
                                        <i className="fas fa-user-edit me-2"></i>
                                        Edit Client Profile: {editFormData.name}
                                    </h3>
                                    <button
                                        className="modal-close"
                                        onClick={() => setShowEditModal(false)}
                                        style={styles.modalClose}
                                    >
                                        <i className="fas fa-times"></i>
                                    </button>
                                </div>
                                
                                <form onSubmit={handleUpdateProfile}>
                                    <div className="modal-body" style={styles.modalBody}>
                                        <h4 style={styles.detailSectionTitle}>Account Information</h4>
                                        <div style={styles.detailGrid}>
                                            <div style={styles.formGroup}>
                                                <label style={styles.formLabel}>Full Name *</label>
                                                <input
                                                    type="text"
                                                    style={styles.formInput}
                                                    value={editFormData.name}
                                                    onChange={(e) => setEditFormData({...editFormData, name: e.target.value})}
                                                    required
                                                    placeholder="Enter client full name"
                                                />
                                            </div>

                                            <div style={styles.formGroup}>
                                                <label style={styles.formLabel}>Email Address *</label>
                                                <input
                                                    type="email"
                                                    style={styles.formInput}
                                                    value={editFormData.email}
                                                    onChange={(e) => setEditFormData({...editFormData, email: e.target.value})}
                                                    required
                                                    placeholder="Enter client email address"
                                                />
                                                <small style={styles.formHelp}>Client will use this email to login</small>
                                            </div>
                                            
                                            <div style={styles.formGroup}>
                                                <label style={styles.formLabel}>Account Status *</label>
                                                <select
                                                    style={styles.formInput}
                                                    value={editFormData.status}
                                                    onChange={(e) => setEditFormData({...editFormData, status: e.target.value})}
                                                    required
                                                >
                                                    <option value="Active">Active</option>
                                                    <option value="Inactive">Inactive</option>
                                                </select>
                                            </div>

                                            <div style={styles.formGroup}>
                                                <label style={styles.formLabel}>User ID</label>
                                                <input
                                                    type="text"
                                                    style={{...styles.formInput, opacity: 0.7, cursor: 'not-allowed'}}
                                                    value={`#${editFormData.id}`}
                                                    disabled
                                                />
                                            </div>
                                        </div>
                                        
                                        <h4 style={styles.detailSectionTitle}>Client Profile Details</h4>
                                        <div style={styles.detailGrid}>
                                            <div style={{...styles.formGroup, gridColumn: '1 / -1'}}>
                                                <label style={styles.formLabel}>Description / Notes</label>
                                                <textarea
                                                    style={{...styles.formInput, minHeight: '100px'}}
                                                    value={editFormData.description}
                                                    onChange={(e) => setEditFormData({...editFormData, description: e.target.value})}
                                                    placeholder="Enter client description or notes..."
                                                    rows="4"
                                                />
                                            </div>

                                            <div style={{...styles.formGroup, gridColumn: '1 / -1'}}>
                                                <label style={styles.formLabel}>Social Media Profile Link</label>
                                                <input
                                                    type="url"
                                                    style={styles.formInput}
                                                    value={editFormData.socialMediaProfile}
                                                    onChange={(e) => setEditFormData({...editFormData, socialMediaProfile: e.target.value})}
                                                    placeholder="https://linkedin.com/in/clientname"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="modal-footer" style={styles.modalFooter}>
                                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%'}}>
                                            <button
                                                type="button"
                                                className="reset-password-btn"
                                                onClick={() => {
                                                    setShowEditModal(false);
                                                    handleEditPassword(editFormData);
                                                }}
                                                style={styles.passwordBtn}
                                            >
                                                <i className="fas fa-key me-1"></i> Change Password
                                            </button>

                                            <div style={{display: 'flex', gap: '10px'}}>
                                                <button
                                                    type="button"
                                                    className="cancel-btn"
                                                    onClick={() => setShowEditModal(false)}
                                                    style={styles.cancelBtn}
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    type="submit"
                                                    className="update-profile-btn"
                                                    disabled={updating}
                                                    style={{
                                                        ...styles.updateBtn,
                                                        opacity: updating ? 0.6 : 1,
                                                        cursor: updating ? 'not-allowed' : 'pointer'
                                                    }}
                                                >
                                                    {updating ? (
                                                        <>
                                                            <span className="spinner" style={styles.spinner}></span>
                                                            Updating...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i className="fas fa-save me-1"></i>
                                                            Update Profile
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                )}

                {/* Password Update Modal */}
                {showPasswordModal && selectedUser && (
                    <div className="modal-overlay" style={styles.modalOverlay}>
                        <div className="modal-container" style={styles.modalContainer}>
                            <div className="modal-card" style={styles.modalCard}>
                                <div className="modal-header" style={styles.modalHeader}>
                                    <h3 style={styles.modalTitle}>
                                        <i className="fas fa-key me-2"></i>
                                        Update Client Password
                                    </h3>
                                    <button
                                        className="modal-close"
                                        onClick={() => setShowPasswordModal(false)}
                                        style={styles.modalClose}
                                    >
                                        <i className="fas fa-times"></i>
                                    </button>
                                </div>
                                <div className="modal-body" style={styles.modalBody}>
                                    <div className="info-alert" style={styles.infoAlert}>
                                        <div className="alert-icon" style={styles.infoAlertIcon}>
                                            <i className="fas fa-info-circle"></i>
                                        </div>
                                        <div className="alert-message" style={styles.infoAlertMessage}>
                                            <strong>Updating password for:</strong>
                                            <span>{selectedUser?.name}</span>
                                        </div>
                                    </div>
                                    
                                    <div className="form-group" style={styles.formGroup}>
                                        <label style={styles.formLabel}>New Password *</label>
                                        <input
                                            type="password"
                                            style={styles.formInput}
                                            value={newPassword}
                                            onChange={(e) => setNewPassword(e.target.value)}
                                            required
                                            placeholder="Enter new password (min 6 characters)"
                                            minLength="6"
                                        />
                                        <div style={styles.formHelp}>
                                            Password must be at least 6 characters long
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer" style={styles.modalFooter}>
                                    <button
                                        className="cancel-btn"
                                        onClick={() => setShowPasswordModal(false)}
                                        disabled={updating}
                                        style={styles.cancelBtn}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        className="update-btn"
                                        onClick={handleUpdatePassword}
                                        disabled={updating || newPassword.length < 6}
                                        style={{
                                            ...styles.updateBtn,
                                            opacity: (updating || newPassword.length < 6) ? 0.6 : 1,
                                            cursor: (updating || newPassword.length < 6) ? 'not-allowed' : 'pointer'
                                        }}
                                    >
                                        {updating ? (
                                            <>
                                                <span className="spinner" style={styles.spinner}></span>
                                                Updating...
                                            </>
                                        ) : (
                                            'Update Password'
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <style jsx>{`
                .spinning {
                    animation: spin 1s linear infinite;
                }

                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }

                @keyframes float {
                    0%, 100% { transform: translate(0px, 0px) scale(1); }
                    33% { transform: translate(30px, -50px) scale(1.1); }
                    66% { transform: translate(-20px, 20px) scale(0.9); }
                }
            `}</style>
        </Layout>
    );
}

// ✅ COMPLETE STYLES (with new styles for search and updated edit modal)
const styles = {
    container: {
        minHeight: '100vh',
        background: `linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)`,
        position: 'relative',
        overflowX: 'hidden'
    },
    animatedBackground: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none'
    },
    floatingOrb: {
        position: 'absolute',
        borderRadius: '50%',
        filter: 'blur(40px)',
        opacity: 0.3,
        animation: 'float 8s ease-in-out infinite'
    },
    orb1: {
        width: '300px',
        height: '300px',
        background: colors.primary,
        top: '10%',
        left: '5%',
        animationDelay: '0s'
    },
    orb2: {
        width: '200px',
        height: '200px',
        background: colors.secondary,
        top: '60%',
        right: '10%',
        animationDelay: '2s'
    },
    orb3: {
        width: '250px',
        height: '250px',
        background: colors.accent,
        bottom: '20%',
        left: '15%',
        animationDelay: '4s'
    },
    orb4: {
        width: '180px',
        height: '180px',
        background: colors.success,
        top: '30%',
        right: '20%',
        animationDelay: '6s'
    },
    gridOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundImage: `linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)`,
        backgroundSize: '50px 50px'
    },
    header: {
        padding: '40px 40px 30px',
        position: 'relative',
        zIndex: 2
    },
    headerContent: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        maxWidth: '1400px',
        margin: '0 auto'
    },
    greeting: {
        display: 'flex',
        alignItems: 'center',
        gap: '20px',
        marginBottom: '15px'
    },
    pageTitle: {
        fontSize: '3rem',
        fontWeight: '800',
        background: 'linear-gradient(135deg, #fff 0%, #a5b4fc 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
        margin: 0
    },
    welcomeBadge: {
        background: 'rgba(255, 255, 255, 0.1)',
        padding: '8px 16px',
        borderRadius: '20px',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        backdropFilter: 'blur(10px)',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    },
    badgeText: {
        color: 'white',
        fontWeight: '600',
        fontSize: '0.9rem'
    },
    badgeRole: {
        background: colors.primary,
        color: 'white',
        padding: '2px 8px',
        borderRadius: '12px',
        fontSize: '0.7rem',
        fontWeight: '600',
        textTransform: 'uppercase'
    },
    pageSubtitle: {
        color: 'rgba(255, 255, 255, 0.8)',
        fontSize: '1.2rem',
        margin: 0,
        maxWidth: '600px'
    },
    headerActions: {
        display: 'flex',
        alignItems: 'center',
        gap: '30px'
    },
    clientCount: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        background: 'rgba(255, 255, 255, 0.1)',
        padding: '10px 16px',
        borderRadius: '15px',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        backdropFilter: 'blur(10px)'
    },
    clientCountText: {
        color: 'rgba(255, 255, 255, 0.8)',
        fontWeight: '600',
        fontSize: '0.9rem'
    },
    clientCountNumber: {
        background: colors.primary,
        color: 'white',
        padding: '4px 12px',
        borderRadius: '20px',
        fontWeight: '700',
        fontSize: '0.9rem'
    },
    refreshBtn: {
        background: 'rgba(255, 255, 255, 0.1)',
        color: 'white',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        padding: '12px 20px',
        borderRadius: '15px',
        cursor: 'pointer',
        fontWeight: '600',
        backdropFilter: 'blur(10px)',
        transition: 'all 0.3s ease',
        display: 'flex',
        alignItems: 'center',
        gap: '10px'
    },
    errorAlert: {
        background: `rgba(${parseInt(colors.danger.slice(1, 3), 16)}, ${parseInt(colors.danger.slice(3, 5), 16)}, ${parseInt(colors.danger.slice(5, 7), 16)}, 0.1)`,
        color: colors.danger,
        padding: '20px',
        borderRadius: '15px',
        margin: '0 40px 30px',
        display: 'flex',
        alignItems: 'center',
        gap: '15px',
        backdropFilter: 'blur(10px)',
        border: `1px solid ${colors.danger}80`,
        maxWidth: '1400px',
        marginLeft: 'auto',
        marginRight: 'auto'
    },
    successAlert: {
        background: `rgba(${parseInt(colors.success.slice(1, 3), 16)}, ${parseInt(colors.success.slice(3, 5), 16)}, ${parseInt(colors.success.slice(5, 7), 16)}, 0.1)`,
        color: colors.success,
        padding: '20px',
        borderRadius: '15px',
        display: 'flex',
        alignItems: 'center',
        gap: '15px',
        backdropFilter: 'blur(10px)',
        border: `1px solid ${colors.success}`
    },
    alertIcon: {
        fontSize: '1.5rem',
        color: colors.danger
    },
    alertMessage: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        color: 'white'
    },
    alertClose: {
        background: 'none',
        border: 'none',
        color: colors.danger,
        cursor: 'pointer',
        padding: '8px',
        borderRadius: '8px',
        transition: 'background 0.3s ease'
    },
    // ✅ NEW: Search Section Styles
    searchSection: {
        marginBottom: '30px'
    },
    searchCard: {
        background: 'rgba(255, 255, 255, 0.05)',
        borderRadius: '20px',
        border: `1px solid rgba(255, 255, 255, 0.1)`,
        backdropFilter: 'blur(10px)',
        padding: '25px 30px'
    },
    searchTitle: {
        fontSize: '1.2rem',
        fontWeight: '700',
        color: 'white',
        marginBottom: '20px',
        display: 'flex',
        alignItems: 'center'
    },
    searchForm: {
        display: 'flex',
        flexDirection: 'column',
        gap: '15px'
    },
    searchInputGroup: {
        display: 'flex',
        gap: '15px',
        alignItems: 'center',
        flexWrap: 'wrap'
    },
    searchField: {
        flex: 1,
        position: 'relative',
        minWidth: '300px'
    },
    searchIcon: {
        position: 'absolute',
        left: '15px',
        top: '50%',
        transform: 'translateY(-50%)',
        color: 'rgba(255, 255, 255, 0.7)',
        fontSize: '1rem'
    },
    searchInput: {
        width: '100%',
        background: 'rgba(255, 255, 255, 0.1)',
        border: `2px solid rgba(255, 255, 255, 0.1)`,
        borderRadius: '10px',
        padding: '12px 15px 12px 45px',
        color: 'white',
        fontSize: '1rem',
        transition: 'all 0.3s ease',
        backdropFilter: 'blur(10px)'
    },
    clearSearchBtn: {
        position: 'absolute',
        right: '15px',
        top: '50%',
        transform: 'translateY(-50%)',
        background: 'none',
        border: 'none',
        color: 'rgba(255, 255, 255, 0.7)',
        cursor: 'pointer',
        padding: '5px',
        fontSize: '0.9rem'
    },
    searchSelect: {
        background: 'rgba(255, 255, 255, 0.1)',
        border: `2px solid rgba(255, 255, 255, 0.1)`,
        borderRadius: '10px',
        padding: '12px 15px',
        color: 'white',
        fontSize: '1rem',
        cursor: 'pointer',
        backdropFilter: 'blur(10px)',
        minWidth: '150px'
    },
    searchButton: {
        background: colors.primary,
        color: 'white',
        border: 'none',
        padding: '12px 25px',
        borderRadius: '10px',
        cursor: 'pointer',
        fontWeight: '600',
        backdropFilter: 'blur(10px)',
        transition: 'all 0.3s ease',
        display: 'flex',
        alignItems: 'center',
        minWidth: '120px',
        justifyContent: 'center'
    },
    searchResultsInfo: {
        padding: '10px 15px',
        background: 'rgba(6, 182, 212, 0.1)',
        borderRadius: '10px',
        border: `1px solid ${colors.accent}`,
        marginTop: '10px'
    },
    searchResultsText: {
        color: 'white',
        fontSize: '0.9rem'
    },
    // ✅ Warning alert style for delete modal
    warningAlert: {
        background: `rgba(${parseInt(colors.danger.slice(1, 3), 16)}, ${parseInt(colors.danger.slice(3, 5), 16)}, ${parseInt(colors.danger.slice(5, 7), 16)}, 0.1)`,
        border: `2px solid ${colors.danger}`,
        borderRadius: '15px',
        padding: '20px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '15px',
        marginBottom: '20px'
    },
    warningAlertIcon: {
        color: colors.danger,
        fontSize: '1.5rem',
        marginTop: '2px'
    },
    warningAlertMessage: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        color: 'white'
    },
    // User details preview for delete modal
    userDetailsPreview: {
        background: 'rgba(255, 255, 255, 0.05)',
        borderRadius: '12px',
        padding: '15px',
        border: '1px solid rgba(255, 255, 255, 0.1)'
    },
    userDetailItem: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: '10px',
        paddingBottom: '10px',
        borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
    },
    userDetailLabel: {
        color: 'rgba(255, 255, 255, 0.7)',
        fontWeight: '600',
        minWidth: '80px',
        fontSize: '0.9rem'
    },
    userDetailValue: {
        color: 'white',
        fontWeight: '500',
        fontSize: '0.9rem',
        marginLeft: '10px'
    },
    // Delete confirm button style
    deleteConfirmBtn: {
        background: colors.danger,
        color: 'white',
        border: 'none',
        padding: '12px 25px',
        borderRadius: '10px',
        cursor: 'pointer',
        fontWeight: '600',
        backdropFilter: 'blur(10px)',
        transition: 'all 0.3s ease',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        minWidth: '140px',
        justifyContent: 'center'
    },
    content: {
        padding: '0 40px 40px',
        maxWidth: '1400px',
        margin: '0 auto',
        position: 'relative',
        zIndex: 2
    },
    clientsTableSection: {
        marginBottom: '40px'
    },
    tableCard: {
        background: 'rgba(255, 255, 255, 0.05)',
        borderRadius: '20px',
        border: `1px solid rgba(255, 255, 255, 0.1)`,
        backdropFilter: 'blur(10px)',
        overflow: 'hidden'
    },
    tableCardHeader: {
        background: colors.gradientMain,
        color: 'white',
        padding: '25px 30px'
    },
    tableCardTitle: {
        fontSize: '1.5rem',
        fontWeight: '700',
        margin: 0,
        display: 'flex',
        alignItems: 'center',
        gap: '10px'
    },
    filterBadge: {
        background: colors.accent,
        color: 'white',
        padding: '2px 8px',
        borderRadius: '10px',
        fontSize: '0.7rem',
        fontWeight: '600'
    },
    tableCardBody: {
        padding: '0'
    },
    loadingState: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '60px 40px',
        color: 'white',
        textAlign: 'center'
    },
    loadingSpinner: {
        width: '40px',
        height: '40px',
        border: '3px solid transparent',
        borderTop: `3px solid ${colors.primary}`,
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
        marginBottom: '20px'
    },
    loadingText: {
        fontSize: '1.8rem',
        margin: '0 0 12px 0',
        fontWeight: '600'
    },
    loadingSubtext: {
        color: 'rgba(255, 255, 255, 0.7)',
        margin: 0,
        fontSize: '1.1rem'
    },
    emptyState: {
        textAlign: 'center',
        padding: '60px 40px',
        color: 'rgba(255, 255, 255, 0.7)'
    },
    emptyIcon: {
        fontSize: '4rem',
        marginBottom: '20px',
        opacity: 0.5
    },
    emptyTitle: {
        color: 'white',
        marginBottom: '10px',
        fontSize: '1.5rem'
    },
    emptyText: {
        marginBottom: '20px',
        fontSize: '1rem'
    },
    tableContainer: {
        overflowX: 'auto'
    },
    clientsTable: {
        width: '100%',
        borderCollapse: 'collapse'
    },
    tableHeader: {
        background: 'rgba(255, 255, 255, 0.1)',
        color: 'white',
        padding: '15px 12px',
        textAlign: 'left',
        fontWeight: '600',
        fontSize: '0.85rem',
        borderBottom: `2px solid rgba(255, 255, 255, 0.1)`,
        whiteSpace: 'nowrap'
    },
    tableRow: {
        borderBottom: `1px solid rgba(255, 255, 255, 0.05)`,
        transition: 'background 0.3s ease'
    },
    tableCell: {
        padding: '12px',
        color: 'white',
        fontSize: '0.85rem',
        borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
    },
    userId: {
        background: 'rgba(255, 255, 255, 0.1)',
        color: 'white',
        padding: '4px 12px',
        borderRadius: '20px',
        fontSize: '0.75rem',
        fontWeight: '600',
        display: 'inline-flex',
        alignItems: 'center'
    },
    userInfo: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
    },
    userAvatar: {
        width: '35px',
        height: '35px',
        borderRadius: '50%',
        background: colors.primary,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        fontSize: '0.9rem'
    },
    userName: {
        fontWeight: '600',
        color: 'white'
    },
    userEmail: {
        color: 'rgba(255, 255, 255, 0.7)',
        fontWeight: '500'
    },
    descriptionText: {
        color: 'rgba(255, 255, 255, 0.7)',
        fontSize: '0.8rem',
        wordBreak: 'break-word',
    },
    socialLink: {
        color: colors.accent,
        textDecoration: 'none',
        fontWeight: '500'
    },
    actionButtons: {
        display: 'flex',
        gap: '8px',
        flexWrap: 'wrap'
    },
    spinner: {
        width: '12px',
        height: '12px',
        border: '2px solid transparent',
        borderTop: '2px solid currentColor',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
    },
    tableFooter: {
        background: 'rgba(255, 255, 255, 0.05)',
        padding: '15px 30px',
        borderTop: `1px solid rgba(255, 255, 255, 0.1)`
    },
    footerContent: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
    },
    footerText: {
        color: 'rgba(255, 255, 255, 0.7)',
        fontSize: '0.85rem',
        fontWeight: '500'
    },
    filterInfo: {
        color: colors.accent,
        marginLeft: '10px',
        fontSize: '0.8rem'
    },
    // Modal Styles
    modalOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px'
    },
    modalContainer: {
        width: '100%',
        maxWidth: '500px'
    },
    modalCard: {
        background: 'rgba(30, 41, 59, 0.95)',
        borderRadius: '20px',
        border: `1px solid rgba(255, 255, 255, 0.1)`,
        backdropFilter: 'blur(20px)',
        overflow: 'hidden',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.8)',
        maxHeight: '90vh',
        overflowY: 'auto'
    },
    modalHeader: {
        background: colors.gradientMain,
        color: 'white',
        padding: '25px 30px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'sticky',
        top: 0,
        zIndex: 10
    },
    modalTitle: {
        fontSize: '1.5rem',
        fontWeight: '700',
        margin: 0
    },
    modalClose: {
        background: 'none',
        border: 'none',
        color: 'white',
        cursor: 'pointer',
        fontSize: '1.2rem',
        padding: '8px',
        borderRadius: '8px',
        transition: 'background 0.3s ease'
    },
    modalBody: {
        padding: '30px',
        maxHeight: '60vh',
        overflowY: 'auto'
    },
    detailSectionTitle: {
        color: colors.accent,
        fontWeight: '700',
        fontSize: '1.1rem',
        marginTop: '15px',
        marginBottom: '10px',
        borderBottom: `1px solid ${colors.accent}50`,
        paddingBottom: '5px'
    },
    detailGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '10px 20px',
        marginTop: '10px',
    },
    formGroup: {
        marginBottom: '20px'
    },
    formLabel: {
        display: 'block',
        color: 'white',
        fontWeight: '600',
        marginBottom: '8px',
        fontSize: '0.9rem'
    },
    formInput: {
        width: '100%',
        background: 'rgba(255, 255, 255, 0.1)',
        border: `2px solid rgba(255, 255, 255, 0.1)`,
        borderRadius: '10px',
        padding: '12px 15px',
        color: 'white',
        fontSize: '1rem',
        transition: 'all 0.3s ease',
        backdropFilter: 'blur(10px)',
        resize: 'none'
    },
    formHelp: {
        color: 'rgba(255, 255, 255, 0.7)',
        fontSize: '0.8rem',
        marginTop: '8px'
    },
    modalFooter: {
        padding: '20px 30px',
        borderTop: `1px solid rgba(255, 255, 255, 0.1)`,
        background: 'rgba(255, 255, 255, 0.05)',
        position: 'sticky',
        bottom: 0,
        zIndex: 10
    },
    passwordBtn: {
        background: colors.primary,
        color: 'white',
        border: 'none',
        padding: '10px 20px',
        borderRadius: '10px',
        cursor: 'pointer',
        fontWeight: '600',
        backdropFilter: 'blur(10px)',
        transition: 'all 0.3s ease',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    },
    cancelBtn: {
        background: 'rgba(255, 255, 255, 0.1)',
        color: 'white',
        border: `1px solid rgba(255, 255, 255, 0.1)`,
        padding: '10px 20px',
        borderRadius: '10px',
        cursor: 'pointer',
        fontWeight: '600',
        backdropFilter: 'blur(10px)',
        transition: 'all 0.3s ease',
        minWidth: '100px'
    },
    updateBtn: {
        background: colors.gradientMain,
        color: 'white',
        border: 'none',
        padding: '10px 20px',
        borderRadius: '10px',
        cursor: 'pointer',
        fontWeight: '600',
        backdropFilter: 'blur(10px)',
        transition: 'all 0.3s ease',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        minWidth: '140px',
        justifyContent: 'center'
    },
    infoAlert: {
        background: 'rgba(6, 182, 212, 0.1)',
        border: `1px solid ${colors.accent}`,
        borderRadius: '12px',
        padding: '15px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '20px'
    },
    infoAlertIcon: {
        color: colors.accent,
        fontSize: '1.2rem'
    },
    infoAlertMessage: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        color: 'white'
    }
};

export default ModifyClients;